import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import HStack from '@jimmyboys/liita-ui/dist/components/HStack'
import VStack from '@jimmyboys/liita-ui/dist/components/VStack'
import AlertBox from 'components/AlertBox/AlertBox'
import HFCheckbox from 'components/HFInputs/HFCheckbox'
import HFDropdown from 'components/HFInputs/HFDropdown'
import HFTextInput from 'components/HFInputs/HFTextInput'

import styles from '../../controllers/Dialog.module.sass'
import { useEffect, useMemo, useState, useRef } from 'react'
import { useExternalFetch } from 'store/hooks'
import { filterSuggestResponse } from 'utils/filterSuggestResponse'
import {
	RequisitionType,
	MedicalActId,
	RequisitionStatus,
} from '../../../../utils/enums'
import { API_URL, parseJSONContent } from 'store/fetchUtils'
import { useFormContext } from 'react-hook-form'
import Icon from '@jimmyboys/liita-ui/dist/components/Icon'
import JustificationField from 'components/JustificationField/JustificationField'
import { useJustifications } from 'hooks/useJustifications'

export const TaskForwardingController = (props) => {
	const { i18n } = useGlobal()
	const {
		outsideNetwork,
		requisitionOutsideNetwork,
		districts,
		serviceProviders,
		providerDescription,
		districtName,
		branch,
		selected_insurance,
		select_act,
		codpost7,
		onUpdateFormData,
		requisitionId,
		injuredBirthday,
		injuredType,
		bodyPart,
		appointmentDate,
		scheduleDate,
		requestingProviderId,
		requisitionType,
		externalSuggestions,
		onBlockResolver = () => {},
		hideJustification = false,
		showWarning = false,
		onProviderPendingChange = () => {},
		requisitionStateId,
		processId,
		appointmentId,
	} = props

	// Choose model to use
	const isImagingRequest = requisitionType === RequisitionType.Imagiology
	const isPhysiotherapyRequest =
		requisitionType === RequisitionType.Physiotherapy
	const isAppointmentRequest = requisitionType === RequisitionType.Appointment

	const getSymbol = (value, className) => {
		const icons = [
			'fast-upward-one',
			'fast-upward-two',
			'fast-downward-one',
			'fast-downward-two',
			'equals',
		]
		const sizes = {
			width: value === 4 ? '16px' : '14px',
			height: value === 4 ? '16px' : '14px',
		}
		if (value < 0 || value > 4) return null
		return (
			<Icon
				name={icons[value]}
				className={styles['price-icon']}
				style={{ fill: className }}
				{...sizes}
			/>
		)
	}

	// FISIOTERAPIA
	const physiotherapyShouldFetch = useMemo(() => {
		const hasValidPhysiotherapyCode = select_act?.includes(
			MedicalActId.PhysiotherapyMedicalActId
		)
		return (
			isPhysiotherapyRequest &&
			hasValidPhysiotherapyCode &&
			branch &&
			selected_insurance &&
			select_act?.length > 0 &&
			codpost7 &&
			(requisitionStateId == null || requisitionStateId !== 2)
		)
	}, [
		isPhysiotherapyRequest,
		select_act,
		branch,
		selected_insurance,
		codpost7,
		requisitionStateId,
	])

	const physiotherapyApiUrl = useMemo(() => {
		return physiotherapyShouldFetch
			? `${API_URL}/api/suggest/physiotherapy/suggestions`
			: null
	}, [physiotherapyShouldFetch])

	const physiotherapyFetchConfig = useMemo(() => {
		if (!physiotherapyShouldFetch) return null

		return {
			method: 'POST',
			body: JSON.stringify({
				branch: branch,
				insuranceId: Number(selected_insurance),
				medicalActId: select_act[0],
				postalCode7: codpost7,
				requestingProviderId: requestingProviderId,
				injuryTypeId: injuredType,
				injuryBodyPartId: bodyPart,
				birthDate: injuredBirthday?.split('T')[0],
			}),
		}
	}, [
		physiotherapyShouldFetch,
		branch,
		selected_insurance,
		select_act,
		codpost7,
		requestingProviderId,
		injuredBirthday,
		injuredType,
		bodyPart,
	])

	const physiotherapyFetchKey = useMemo(() => {
		if (!physiotherapyShouldFetch) return null
		return `physio-${branch}-${selected_insurance}-${select_act.join(
			','
		)}-${codpost7}`
	}, [
		physiotherapyShouldFetch,
		branch,
		selected_insurance,
		select_act,
		codpost7,
	])

	// IMAGIOLOGIA
	const imagingShouldFetch = useMemo(() => {
		return (
			isImagingRequest &&
			branch &&
			selected_insurance &&
			select_act?.length > 0 &&
			codpost7 &&
			appointmentDate &&
			scheduleDate &&
			(requisitionStateId == null || requisitionStateId !== 2)
		)
	}, [
		isImagingRequest,
		branch,
		selected_insurance,
		select_act,
		codpost7,
		appointmentDate,
		scheduleDate,
		requisitionStateId,
	])

	const imagingApiUrl = useMemo(() => {
		return imagingShouldFetch
			? `${API_URL}/api/suggest/imaging/suggestions`
			: null
	}, [imagingShouldFetch])

	const imagingFetchConfig = useMemo(() => {
		if (!imagingShouldFetch) return null

		return {
			method: 'POST',
			body: JSON.stringify({
				branch: branch,
				insuranceID: Number(selected_insurance),
				codes: select_act,
				postalCode7: codpost7,
				requestingProviderId: requestingProviderId,
				appointmentDate: appointmentDate
					? appointmentDate.split('T')[0] + 'T00:00:00'
					: undefined,
				scheduleDate: scheduleDate
					? scheduleDate.split('T')[0] + 'T00:00:00'
					: undefined,
			}),
		}
	}, [
		imagingShouldFetch,
		branch,
		selected_insurance,
		select_act,
		codpost7,
		requestingProviderId,
		appointmentDate,
		scheduleDate,
	])

	const imagingFetchKey = useMemo(() => {
		if (!imagingShouldFetch) return null
		return `imaging-${branch}-${selected_insurance}-${select_act.join(
			','
		)}-${codpost7}-${appointmentDate || 'none'}-${scheduleDate || 'none'}`
	}, [
		imagingShouldFetch,
		branch,
		selected_insurance,
		select_act,
		codpost7,
		appointmentDate,
		scheduleDate,
	])

	const apiUrl = isImagingRequest ? imagingApiUrl : physiotherapyApiUrl
	const fetchConfig = isImagingRequest
		? imagingFetchConfig
		: physiotherapyFetchConfig
	const fetchKey = isImagingRequest ? imagingFetchKey : physiotherapyFetchKey

	const {
		data: response,
		loading,
		error,
	} = useExternalFetch(apiUrl, fetchConfig, [fetchKey])

	const suggestions = externalSuggestions || response

	const usernameRef = useRef(
		JSON.parse(localStorage.getItem('user')).username
	)
	const username = usernameRef.current

	const lastResponseRef = useRef(null)

	const { setValue, watch } = useFormContext()

	// ── Connection link (only for Appointment/Consulta type) ───────────────────
	const [loadingConnectionOptions, setLoadingConnectionOptions] =
		useState(false)
	const [hasFetchedConnection, setHasFetchedConnection] = useState(false)
	const [connectionOptions, setConnectionOptions] = useState([])
	const [selectedConnectionOption, setSelectedConnectionOption] =
		useState(null)
	const [decidirMaisTarde, setDecidirMaisTarde] = useState(false)
	const [connectionError, setConnectionError] = useState(false)
	const [connectionMessage, setConnectionMessage] = useState(null)
	const [warningAppointmentId, setWarningAppointmentId] = useState(null)

	const showConnectionSection =
		loadingConnectionOptions ||
		(hasFetchedConnection &&
			(connectionOptions.length > 0 ||
				connectionMessage !== null ||
				warningAppointmentId !== null))

	const formatConnectionDate = (iso) => {
		if (!iso) return ''
		return new Date(iso).toLocaleDateString('pt-PT')
	}

	useEffect(() => {
		if (!isAppointmentRequest || !processId || !appointmentId) return
		if (requisitionStateId !== RequisitionStatus.ClinicallyRefused) return

		const token =
			localStorage.getItem('session-token') ||
			sessionStorage.getItem('session-token')
		if (!token) return

		setLoadingConnectionOptions(true)
		const qs = requisitionId ? `?requisitionId=${requisitionId}` : ''
		fetch(
			`${API_URL}/api/clinical_processes/${processId}/appointments/${appointmentId}/cancellation-link${qs}`,
			{ method: 'GET', headers: { Authorization: `Bearer ${token}` } }
		)
			.then((r) => parseJSONContent(r))
			.then((res) => {
				if (!res?.mensagem) {
					const opts = res?.connectionOptions ?? []
					setConnectionOptions(opts)
					if (res?.warningAppointmentId) {
						setWarningAppointmentId(res.warningAppointmentId)
					}
					if (opts.length === 0) {
						setDecidirMaisTarde(true)
						setValue('decidirMaisTarde', true)
					}
				} else {
					setConnectionMessage(res.mensagem)
					setDecidirMaisTarde(true)
					setValue('decidirMaisTarde', true)
				}
			})
			.catch(() => {
				setDecidirMaisTarde(true)
				setValue('decidirMaisTarde', true)
			})
			.finally(() => {
				setLoadingConnectionOptions(false)
				setHasFetchedConnection(true)
				setValue('hasFetchedConnection', true)
			})
	}, [])
	// ──────────────────────────────────────────────────────────────────────────
	const [selectedProvider, setSelectedProvider] = useState(null)
	const providerId = watch('providerId')

	const [pendingProvider, setPendingProvider] = useState(null)
	const [selectedUserJustification, setSelectedUserJustification] =
		useState(null)
	const [selectedUserJustificationId, setSelectedUserJustificationId] =
		useState(null)

	const onUpdateFormDataRef = useRef(onUpdateFormData)
	onUpdateFormDataRef.current = onUpdateFormData
	const prevDataToSendRef = useRef(null)

	const justificationCategory = isImagingRequest
		? 1
		: isPhysiotherapyRequest
		? 2
		: 3
	const { justifications } = useJustifications(justificationCategory)

	const handleJustificationChange = (justification) => {
		if (!justification) {
			setSelectedUserJustification(null)
			setSelectedUserJustificationId(null)
			return
		}
		setSelectedUserJustification(justification.justificationName)
		setSelectedUserJustificationId(justification.justificationId)
	}

	const handleJustificationCancel = () => {
		setPendingProvider(null)
		setSelectedProvider(null)
		setSelectedUserJustification(null)
		setSelectedUserJustificationId(null)
		setValue('providerId', null)
		onProviderPendingChange(null)
	}

	const isBlocked =
		!hideJustification &&
		pendingProvider !== null &&
		!selectedUserJustification
	const firstBlockRef = useRef(true)

	useEffect(() => {
		if (firstBlockRef.current) {
			firstBlockRef.current = false
			return
		}
		onBlockResolver(isBlocked ? 1 : -1)
	}, [isBlocked])

	useEffect(() => {
		if (selectedProvider === null) return
		const provider = suggestions?.providerReferrals?.[selectedProvider]
		if (!provider || providerId !== provider.providerId.toString()) {
			setSelectedProvider(null)
		}
	}, [providerId])

	const dataToSend = useMemo(() => {
		if (!suggestions) return null

		if (isImagingRequest) {
			return {
				branch: branch,
				insuranceID: Number(selected_insurance),
				codes: select_act,
				postalCode7: codpost7,
				processId: Number(window.location.pathname.split('/')[2]),
				username: username,
				response: response,
				requestingProviderId: requestingProviderId,
				requisitionIds: requisitionId,
				injuredBirthday: injuredBirthday?.split('T')[0],
				injuredType: injuredType,
				bodyPart: bodyPart,
				userJustification: selectedUserJustification,
				userJustificationId: selectedUserJustificationId,
			}
		} else {
			return {
				branch: branch,
				insuranceId: Number(selected_insurance),
				medicalActId: select_act,
				postalCode7: codpost7,
				processId: Number(window.location.pathname.split('/')[2]),
				username: username,
				response: response,
				requisitionIds: requisitionId,
				injuredBirthday: injuredBirthday?.split('T')[0],
				injuredType: injuredType,
				bodyPart: bodyPart,
				userJustification: selectedUserJustification,
				userJustificationId: selectedUserJustificationId,
			}
		}
	}, [
		response,
		isImagingRequest,
		selectedUserJustification,
		selectedUserJustificationId,
		branch,
		selected_insurance,
		select_act,
		codpost7,
		username,
		requisitionId,
		injuredBirthday,
		injuredType,
		bodyPart,
		requestingProviderId,
	])

	useEffect(() => {
		if (response && dataToSend && onUpdateFormDataRef.current) {
			const currentSerialized = JSON.stringify(dataToSend)
			const prevSerialized = JSON.stringify(
				prevDataToSendRef.current || {}
			)

			if (currentSerialized !== prevSerialized) {
				prevDataToSendRef.current = dataToSend
				onUpdateFormDataRef.current(dataToSend)
			}
		}
	}, [dataToSend])

	const [finalMsg, setFinalMsg] = useState('')

	useEffect(() => {
		if (!response) return

		if (isImagingRequest || isPhysiotherapyRequest) {
			setFinalMsg(response.description || '')
		} else {
			if (response.split(',').length > 2) {
				const responseRefactored = response?.replace(',', ' ')
				setFinalMsg(
					responseRefactored
						.split(',')[0]
						.replace(/['"()]/g, '')
						.trim()
				)
			} else {
				setFinalMsg(
					response
						?.split(',')[0]
						.replace(/['"()]/g, '')
						.trim()
				)
			}
		}
	}, [response, isImagingRequest])

	const connectionErrorFromForm = watch('connectionError')

	return (
		<>
			{isAppointmentRequest && showConnectionSection && (
				<fieldset>
					<h5 className={styles['input-title']}>
						{i18n`Decision link appointment`}
					</h5>

					{loadingConnectionOptions && (
						<p className={styles['connection-info']}>
							{i18n`Loading`}...
						</p>
					)}

					{!loadingConnectionOptions && (
						<>
							{warningAppointmentId &&
								connectionOptions.length > 0 && (
									<p className={styles['warning-message']}>
										{i18n`A consulta`}{' '}
										{warningAppointmentId}{' '}
										{i18n`vai ficar sem ligação a uma consulta destino. A qual pretende ligar?`}
									</p>
								)}

							<div className={styles['cancellation-options']}>
								<div className={styles['options-list']}>
									{connectionOptions.length === 0 && (
										<p
											className={
												styles['connection-info']
											}
										>
											{connectionMessage ??
												i18n`Não há consultas disponíveis para fazer a ligação`}
										</p>
									)}

									{connectionOptions.map((opt) => (
										<div
											key={opt.appointmentId}
											className={styles['option-item']}
										>
											<label>
												<input
													type='radio'
													name='connectionOption'
													value={opt.appointmentId}
													checked={
														selectedConnectionOption?.appointmentId ===
														opt.appointmentId
													}
													onChange={() => {
														setSelectedConnectionOption(
															opt
														)
														setDecidirMaisTarde(
															false
														)
														setConnectionError(
															false
														)
														setValue(
															'transferAppointments',
															[
																Number(
																	opt.appointmentId
																),
															]
														)
														setValue(
															'decidirMaisTarde',
															false
														)
														setValue(
															'connectionError',
															false
														)
													}}
												/>
												<div
													className={
														styles['option-details']
													}
												>
													<strong>
														#{opt.appointmentId}
													</strong>{' '}
													–{' '}
													{formatConnectionDate(
														opt.date
													)}
												</div>
											</label>
										</div>
									))}

									<div className={styles['option-item']}>
										<label>
											<input
												type='radio'
												name='connectionOption'
												value='later'
												checked={decidirMaisTarde}
												onChange={() => {
													setDecidirMaisTarde(true)
													setSelectedConnectionOption(
														null
													)
													setConnectionError(false)
													setValue(
														'transferAppointments',
														[]
													)
													setValue(
														'decidirMaisTarde',
														true
													)
													setValue(
														'connectionError',
														false
													)
												}}
											/>
											<div
												className={
													styles['option-details']
												}
											>
												{i18n`Decide later`}
											</div>
										</label>
									</div>
								</div>

								{(connectionError ||
									connectionErrorFromForm) && (
									<div className={styles['selection-error']}>
										⚠{' '}
										{i18n`Por favor selecione uma opção de ligação`}
									</div>
								)}
							</div>
						</>
					)}
				</fieldset>
			)}

			<fieldset className={styles['provider']}>
				<VStack>
					<h4>{i18n`Provider`}</h4>

					{requisitionOutsideNetwork && providerDescription && (
						<AlertBox
							className={styles['alert-box']}
							type='info'
							items={[
								{
									title: i18n`Provider Suggestion`,
									text: `${districtName} - ${providerDescription}`,
								},
							]}
						/>
					)}

					<VStack gap='xsmall'>
						<HStack justify='space-between'>
							<h5>{i18n`Service provider`}</h5>
							<HFCheckbox
								className={styles['checkbox']}
								name='outsideNetwork'
								label={i18n`Outside Network?`}
							/>
						</HStack>

						{outsideNetwork ? (
							<HStack
								className={styles['provider-fields']}
								gap='xsmall'
							>
								<HFTextInput
									name='externalProvider.name'
									placeholder={i18n`Provider Name`}
									noResize
								/>
								<HFDropdown
									name='externalProvider.districtId'
									placeholder={i18n`District`}
									options={districts || []}
									searchable={true}
									optionsAlign='right'
									noData={i18n`No results`}
								/>
							</HStack>
						) : (
							<HFDropdown
								name='providerId'
								placeholder={i18n`Service provider`}
								options={serviceProviders || []}
								searchable={true}
								optionsAlign='right'
								noData={i18n`No results`}
								onChange={(e) => {
									const selectedProviderId = e?.target?.value
									if (!selectedProviderId) {
										setValue(
											'providerId',
											selectedProviderId
										)
										setSelectedProvider(null)
										return
									}

									const hasSuggestions =
										suggestions?.providerReferrals?.length >
										0
									const parsedId = parseInt(
										selectedProviderId,
										10
									)

									if (!hasSuggestions) {
										setValue(
											'providerId',
											selectedProviderId
										)
										setSelectedProvider(null)
										return
									}

									const firstProvider =
										suggestions.providerReferrals[0]
									const selectedProvider =
										suggestions.providerReferrals.find(
											(p) =>
												Number(p.providerId) ===
												parsedId
										)

									if (!selectedProvider) {
										setValue(
											'providerId',
											selectedProviderId
										)
										const manualProvider = {
											providerId: parsedId,
											providerName: 'Manual Selection',
										}
										setPendingProvider(manualProvider)
										onProviderPendingChange(manualProvider)
										return
									}

									const isFirstProvider = firstProvider
										? Number(firstProvider.providerId) ===
										  Number(selectedProvider.providerId)
										: false

									if (isFirstProvider) {
										setValue(
											'providerId',
											selectedProviderId
										)
										const providerIndex =
											suggestions.providerReferrals.findIndex(
												(p) =>
													Number(p.providerId) ===
													parsedId
											)
										setSelectedProvider(providerIndex)
										setSelectedUserJustification(
											'Sugestao do modelo.'
										)
										setSelectedUserJustificationId(null)
										setPendingProvider(null)
										onProviderPendingChange(null)
									} else {
										setValue(
											'providerId',
											selectedProviderId
										)
										const providerIndex =
											suggestions.providerReferrals.findIndex(
												(p) =>
													Number(p.providerId) ===
													parsedId
											)
										setSelectedProvider(providerIndex)
										setPendingProvider(selectedProvider)
										onProviderPendingChange(
											selectedProvider
										)
									}
								}}
							/>
						)}
						{loading ? (
							<p>{i18n`loading suggestion`}</p>
						) : response ? (
							<>
								<VStack>
									<HStack>
										<svg
											xmlns='http://www.w3.org/2000/svg'
											viewBox='0 0 24 24'
											fill='currentColor'
											width='24'
											height='24'
											className={styles['referral-icon']}
										>
											<path d='M12 2a1 1 0 011 1v1h4a2 2 0 012 2v3h1a1 1 0 110 2h-1v7a2 2 0 01-2 2h-2v1a1 1 0 11-2 0v-1H9v1a1 1 0 11-2 0v-1H5a2 2 0 01-2-2v-7H2a1 1 0 110-2h1V6a2 2 0 012-2h4V3a1 1 0 011-1zm-4 7a1 1 0 100 2 1 1 0 000-2zm8 0a1 1 0 100 2 1 1 0 000-2zm-8 4h8v5H8v-5z' />
										</svg>
										<h4 className={styles['noMargin']}>
											{i18n`Trust AI Tittle`}
										</h4>
									</HStack>
									<h4>{i18n`Trust AI Suggestion`}</h4>
								</VStack>
								<h6 className={styles['noMargin']}>
									{i18n`Trust AI Sections`}
								</h6>
								{isImagingRequest || isPhysiotherapyRequest ? (
									<>
										{suggestions?.providerReferrals?.map(
											(p, index) => (
												<div
													key={index}
													className={
														styles['referral-row']
													}
												>
													<div
														className={
															styles[
																'referral-info'
															]
														}
													>
														<p
															className={
																styles[
																	'referral-name'
																]
															}
														>
															{p.providerName}
														</p>
														<small
															className={
																styles[
																	'referral-details'
																]
															}
														>
															{(() => {
																const percentagem =
																	((p.retailPrice -
																		p.price) /
																		p.price) *
																	100
																const className =
																	percentagem <=
																	0
																		? '#8B0000'
																		: percentagem <=
																		  10
																		? '#d3b610'
																		: '#23a823'
																const number =
																	percentagem <=
																	-10
																		? 3
																		: percentagem <=
																		  0
																		? 2
																		: percentagem <=
																		  10
																		? 4
																		: percentagem <=
																		  30
																		? 0
																		: 1
																return getSymbol(
																	number,
																	className
																)
															})()}
															<span>
																{p.price.toFixed(
																	2
																)}{' '}
																€ |{' '}
																{(
																	p.distance /
																	1000
																).toFixed(
																	2
																)}{' '}
																km |{' '}
																{p.averageTime.toFixed(
																	2
																)}{' '}
																d
															</span>
														</small>
													</div>
													<button
														type='button'
														onClick={() => {
															if (
																selectedProvider ===
																index
															) {
																setSelectedProvider(
																	null
																)
																setValue(
																	'providerId',
																	null
																)
																setPendingProvider(
																	null
																)
																setSelectedUserJustification(
																	null
																)
																setSelectedUserJustificationId(
																	null
																)
																onProviderPendingChange(
																	null
																)
																return
															}
															const firstProvider =
																suggestions
																	?.providerReferrals?.[0]
															const isFirstProvider =
																firstProvider
																	? Number(
																			firstProvider.providerId
																	  ) ===
																	  Number(
																			p.providerId
																	  )
																	: false
															if (
																isFirstProvider
															) {
																setValue(
																	'providerId',
																	p.providerId.toString()
																)
																setSelectedProvider(
																	index
																)
																setSelectedUserJustification(
																	'Sugestao do modelo.'
																)
																setSelectedUserJustificationId(
																	null
																)
																setPendingProvider(
																	null
																)
																onProviderPendingChange(
																	null
																)
															} else {
																setValue(
																	'providerId',
																	p.providerId.toString()
																)
																setSelectedProvider(
																	index
																)
																setPendingProvider(
																	p
																)
																onProviderPendingChange(
																	p
																)
															}
														}}
														className={`${
															styles[
																'referral-select-btn'
															]
														}${
															selectedProvider ===
															index
																? ` ${styles['referral-select-btn-selected']}`
																: ''
														}`}
													>
														<span>✓</span>
													</button>
												</div>
											)
										)}
									</>
								) : (
									filterSuggestResponse(finalMsg)
								)}
							</>
						) : null}
					</VStack>
				</VStack>

				{!hideJustification && pendingProvider && (
					<>
						{showWarning && !selectedUserJustification && (
							<AlertBox
								className={styles['alert-box']}
								type='warning'
								items={[
									{
										title: i18n`Warning`,
										text: i18n`Justification is mandatory to proceed`,
									},
								]}
							/>
						)}
						<JustificationField
							justifications={justifications}
							providerName={
								pendingProvider?.providerName ||
								pendingProvider?.provider?.providerName
							}
							onJustificationChange={handleJustificationChange}
							onCancel={handleJustificationCancel}
						/>
					</>
				)}
			</fieldset>
		</>
	)
}
